import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { applyLanguage, LANGUAGES } from '../lib/translate';

const STORAGE_KEY = 'osho_lang';
const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem(STORAGE_KEY) || 'en');
  const [translating, setTranslating] = useState(false);
  const location = useLocation();
  const lastPath = useRef('');

  const setLang = useCallback(async (next) => {
    localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
    setTranslating(true);
    try { await applyLanguage(next); } finally { setTranslating(false); }
  }, []);

  // Re-apply the chosen language after each route change so new page content is translated.
  useEffect(() => {
    if (lang === 'en') return;
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    const id = setTimeout(() => { applyLanguage(lang); }, 250);
    return () => clearTimeout(id);
  }, [location.pathname, lang]);

  // Apply persisted language on first load.
  useEffect(() => {
    if (lang !== 'en') {
      const id = setTimeout(() => { applyLanguage(lang); }, 400);
      return () => clearTimeout(id);
    }
    return undefined;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LanguageContext.Provider value={{ lang, setLang, translating, languages: LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
